import { Injectable } from '@angular/core';
import { InteractionRequiredAuthError, AuthenticationResult, AccountInfo, BrowserAuthError } from '@azure/msal-browser';
import { MsalService } from '@azure/msal-angular';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AuthDispatcher } from '../store/auth/auth-dispatcher';
import { environment } from '../../environments/environment';
import { FinegrainRolesService } from '../common/services/finegrain-roles/finegrain-roles.service';

@Injectable()
export class AuthService {
  constructor(
    private readonly msalService: MsalService,
    private readonly authDispatcher: AuthDispatcher,
    private readonly finegrainRolesService: FinegrainRolesService
  ) { }

  public initialize(): void {
    this.restoreActiveAccount();
    this.syncUserStoreFromActiveAccount();
    if (this.msalService.instance.getActiveAccount()) {
      this.recordLoginTime();
    }
  }

  public handleRedirect(): Observable<AuthenticationResult | null> {
    return from(this.msalService.instance.handleRedirectPromise()).pipe(
      tap((result) => {
        if (result && result.account) {
          this.msalService.instance.setActiveAccount(result.account);
          this.recordLoginTime();
        }
      }),
      tap(() => {
        this.restoreActiveAccount();
        this.syncUserStoreFromActiveAccount();
      }),
      catchError((error) => {
        if (this.isInteractionInProgress() || this.isInteractionInProgressError(error)) {
          return of(null);
        }
        if (error instanceof InteractionRequiredAuthError) {
          this.login();
        }
        return throwError(() => error);
      })
    );
  }

  private static readonly LOGOUT_TIME_KEY = 'auth.logoutTime';
  private static readonly LOGIN_TIME_KEY = 'auth.loginTime';
  private wasLoggedOutAfterLastLogin(): boolean {
    try {
      const logoutTime = parseInt(localStorage.getItem(AuthService.LOGOUT_TIME_KEY) || '0', 10);
      const loginTime = parseInt(localStorage.getItem(AuthService.LOGIN_TIME_KEY) || '0', 10);
      return logoutTime > 0 && logoutTime > loginTime;
    } catch (_) { return false; }
  }
  private recordLoginTime(): void {
    try { localStorage.setItem(AuthService.LOGIN_TIME_KEY, String(Date.now())); } catch (_) { }
  }

  public login(forcePrompt = false): void {
    if (this.isInteractionInProgress()) {
      return;
    }
    const request: any = { scopes: environment.us_config.consentScopes };
    if (forcePrompt || this.wasLoggedOutAfterLastLogin()) {
      request.prompt = 'login';
    }
    this.msalService.loginRedirect(request).subscribe({
      error: (error) => {
        if (!this.isInteractionInProgressError(error)) {
          throw error;
        }
      }
    });
  }

  public logout(): void {
    this.authDispatcher.logout();
    this.authDispatcher.setToken('');
    try { localStorage.setItem(AuthService.LOGOUT_TIME_KEY, String(Date.now())); } catch (_) { }
    try { localStorage.setItem('app.logout', String(Date.now())); } catch (_) { }

    const account = this.msalService.instance.getActiveAccount();
    this.msalService.logoutRedirect({
      account: account ?? undefined,
      onRedirectNavigate: () => {
        window.location.href = `${window.location.origin}/dashboard`;
        return false;
      }
    });
  }

  public logoutBroadcastReceived(): void {
    this.authDispatcher.logout();
    this.authDispatcher.setToken('');
    sessionStorage.removeItem('msal.interaction.status');
    window.location.href = `${window.location.origin}/dashboard`;
  }

  public isLoggedIn(): boolean {
    this.restoreActiveAccount();
    return !!this.msalService.instance.getActiveAccount();
  }

  public acquireAccessToken(): Observable<string> {
    const account = this.getOrRestoreActiveAccount();
    if (!account) {
      this.login();
      return throwError(() => 'No active account available');
    }

    return from(this.msalService.instance.acquireTokenSilent({
      account,
      scopes: environment.us_config.consentScopes
    })).pipe(
      tap((result) => this.onAuthResult(result)),
      map((result) => this.resolveApiToken(result)),
      catchError((error) => {
        if (error instanceof InteractionRequiredAuthError) {
          this.login();
        }
        return throwError(() => error);
      })
    );
  }

  public refreshTokenIfNeeded(): Observable<string> {
    return this.acquireAccessToken().pipe(
      switchMap((token) => token ? of(token) : throwError(() => 'Empty token'))
    );
  }

  public setUserDetails(): void {
    this.syncUserStoreFromActiveAccount();
  }

  private onAuthResult(result: AuthenticationResult): void {
    if (result.account) {
      this.msalService.instance.setActiveAccount(result.account);
    }
    this.authDispatcher.setToken(this.resolveApiToken(result));
    this.syncUserStoreFromActiveAccount();
  }

  private resolveApiToken(result: AuthenticationResult): string {
    if (this.shouldUseLegacyIdTokenFallback()) {
      return result.idToken || result.accessToken || '';
    }
    if (result.accessToken) {
      return result.accessToken;
    }
    if (result.idToken) {
      return result.idToken;
    }
    return '';
  }

  private shouldUseLegacyIdTokenFallback(): boolean {
    const scopes = environment.us_config.consentScopes || [];
    return scopes.length === 1 && scopes[0].toLowerCase() === 'openid';
  }

  private restoreActiveAccount(): void {
    const active = this.msalService.instance.getActiveAccount();
    if (active) {
      return;
    }
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  private getOrRestoreActiveAccount(): AccountInfo | null {
    this.restoreActiveAccount();
    return this.msalService.instance.getActiveAccount();
  }

  private syncUserStoreFromActiveAccount(): void {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      this.authDispatcher.setLogin(false);
      return;
    }

    this.authDispatcher.setLogin(true);

    const claims: any = account.idTokenClaims || {};
    const roleClaims = claims.roles || claims.role || [];
    const availableRoles = Array.isArray(roleClaims) ? roleClaims : [roleClaims];
    const user = {
      preferred_username: account.username,
      name: claims.name || account.name || account.username,
      roles: availableRoles,
      availableRoles
    };
    this.authDispatcher.setUserDetails(user);
    this.finegrainRolesService.setPermissions();
  }

  public isInteractionInProgress(): boolean {
    const interactionValue = sessionStorage.getItem('msal.interaction.status');
    if (interactionValue && interactionValue.toLowerCase().indexOf('interaction') > -1) {
      return true;
    }
    return false;
  }

  private isInteractionInProgressError(error: any): boolean {
    return error instanceof BrowserAuthError
      && error.errorCode === 'interaction_in_progress';
  }
}